import React from 'react';
import StudioEditor from './StudioEditor';
import usePageRenderer from './hooks/usePageRenderer';
import './DocumentStudio.css';

/**
 * Document Studio — page-level restructuring (rotate, delete, reorder, split).
 *
 * The page count comes from the PDF this component loads, and from nowhere
 * else. It used to arrive as a `pageCount` prop from ReviewDetailPage; the
 * state behind that prop lost its only writer in 46c43e9 (2026-04-09) and sat
 * at its initial value of 1 from then on, so the studio modelled every
 * document as a single page — no split handles, and cuts that described a
 * document that did not exist. A prop chain that can silently go stale is not
 * worth restoring when the renderer already knows the answer.
 *
 * This outer component owns the renderer and the load surfaces; StudioEditor
 * owns the page model and is not mounted until the count is known.
 */
const DocumentStudio = ({
  documentId,
  documentUrl,
  documentName,
  aiSplitSuggestions = [],
  onApply,
  onCancel,
}) => {
  const {
    loading,
    error,
    pageCount,
    renderThumbnail,
    renderPreview,
  } = usePageRenderer(documentUrl);

  if (loading) {
    return (
      <div className="ds-overlay">
        <div className="ds-loading">
          <div className="ds-spinner" />
          <p>Loading Document Studio...</p>
        </div>
      </div>
    );
  }

  // A loaded document reporting no pages is as unusable as one that failed to
  // load — an editor over zero pages would offer operations on nothing.
  if (error || !pageCount) {
    return (
      <div className="ds-overlay">
        <div className="ds-error">
          <h3>Failed to Load Document</h3>
          <p>{error || 'This document has no pages to edit.'}</p>
          <button className="ds-btn-primary" onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <StudioEditor
      documentName={documentName}
      pageCount={pageCount}
      aiSplitSuggestions={aiSplitSuggestions}
      renderThumbnail={renderThumbnail}
      renderPreview={renderPreview}
      onApply={onApply}
      onCancel={onCancel}
    />
  );
};

export default DocumentStudio;
